/**
 * WindowRefService - Utility for opening new tabs/windows
 * Provides a consistent way to open external links across the application
 */

export class WindowRefService {
  /**
   * Opens a URL in a new tab
   * @param url The URL to open
   * @param target The target window (defaults to '_blank')
   * @param features Optional window features
   */
  static openInNewTab(url: string, target: string = '_blank', features?: string): Window | null {
    if (typeof window === 'undefined') {
      console.warn('WindowRefService: Cannot open URL, window is not available');
      return null;
    }

    try {
      // Validate URL
      if (!url || typeof url !== 'string') {
        console.warn('WindowRefService: Invalid URL provided', url);
        return null;
      }

      // Open in new tab/window
      const newWindow = window.open(url, target, features);
      
      if (!newWindow) {
        console.warn('WindowRefService: Failed to open new window/tab - popup may be blocked');
        return null;
      }

      return newWindow;
    } catch (error) {
      console.error('WindowRefService: Error opening URL', error);
      return null;
    }
  }

  /**
   * Opens a URL in a new tab with default settings
   * @param url The URL to open
   */
  static openUrl(url: string): Window | null {
    return this.openInNewTab(url);
  }

  /**
   * Checks if the current environment supports opening new windows/tabs
   */
  static isSupported(): boolean {
    return typeof window !== 'undefined' && typeof window.open === 'function';
  }

  /**
   * Checks if popups are blocked by the browser
   */
  static async checkPopupBlocked(): Promise<boolean> {
    if (!this.isSupported()) {
      return true;
    }

    try {
      const testWindow = window.open('', '_blank');
      if (testWindow) {
        testWindow.close();
        return false;
      }
      return true;
    } catch {
      return true;
    }
  }
}

// Export a default instance for convenience
export const windowRefService = WindowRefService; 